/**
 * AdminPanelModal - Admin panel for managing departments and operators
 * Extracted from App.js for better maintainability
 */
import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { 
  RefreshCw, X, Plus, Trash2, Factory, User, 
  Shield, Keyboard, FileText, Play, Pause 
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminPanelModal = ({ isOpen, onClose }) => {
  const [activeTab, setActiveTab] = useState('departments');
  const [departments, setDepartments] = useState([]);
  const [operators, setOperators] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Form states
  const [showAddDept, setShowAddDept] = useState(false);
  const [showAddOperator, setShowAddOperator] = useState(false);
  const [editingDept, setEditingDept] = useState(null);
  const [editingOperator, setEditingOperator] = useState(null);
  
  // New department form
  const [newDeptName, setNewDeptName] = useState('');
  const [newDeptPassword, setNewDeptPassword] = useState('');
  
  // New operator form
  const [newOpName, setNewOpName] = useState('');
  const [newOpDeptId, setNewOpDeptId] = useState('');
  
  // Edit forms
  const [editDeptPassword, setEditDeptPassword] = useState('');
  const [editOpName, setEditOpName] = useState('');
  const [editOpDeptId, setEditOpDeptId] = useState('');

  const fetchData = useCallback(async () => {
    try {
      const [deptRes, opRes] = await Promise.all([
        axios.get(`${API}/admin/departments`, { withCredentials: true }),
        axios.get(`${API}/admin/department-operators`, { withCredentials: true })
      ]);
      
      setDepartments(deptRes.data.departments || []);
      setOperators(opRes.data.operators || []);
    } catch (error) {
      console.error('Failed to load admin data:', error);
      toast.error('Failed to load admin data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (isOpen) {
      fetchData();
    }
  }, [isOpen, fetchData]);

  const handleAddDepartment = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/admin/departments`, 
        { name: newDeptName, password: newDeptPassword },
        { withCredentials: true }
      );
      toast.success(`Department "${newDeptName}" created`);
      setNewDeptName('');
      setNewDeptPassword('');
      setShowAddDept(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create department');
    }
  };

  const handleUpdateDeptPassword = async (deptId) => {
    try {
      await axios.put(`${API}/admin/departments/${deptId}`,
        { password: editDeptPassword },
        { withCredentials: true }
      );
      toast.success('Password updated');
      setEditingDept(null);
      setEditDeptPassword('');
    } catch (error) {
      toast.error('Failed to update password');
    }
  };

  const handleDeleteDepartment = async (dept) => {
    if (!window.confirm(`Delete department "${dept.name}" and all its operators?`)) return;
    try {
      await axios.delete(`${API}/admin/departments/${dept.id}`,
        { withCredentials: true }
      );
      toast.success(`Department "${dept.name}" deleted`);
      fetchData();
    } catch (error) {
      toast.error('Failed to delete department');
    }
  };

  const handleAddOperator = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/admin/department-operators`,
        { name: newOpName, department_id: newOpDeptId },
        { withCredentials: true }
      );
      toast.success(`Operator "${newOpName}" added`);
      setNewOpName('');
      setNewOpDeptId('');
      setShowAddOperator(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add operator');
    }
  };

  const handleUpdateOperator = async (opId) => {
    try {
      const updateData = {};
      if (editOpName) updateData.name = editOpName;
      if (editOpDeptId) updateData.department_id = editOpDeptId;
      
      await axios.put(`${API}/admin/department-operators/${opId}`,
        updateData,
        { withCredentials: true }
      );
      toast.success('Operator updated');
      setEditingOperator(null);
      setEditOpName('');
      setEditOpDeptId('');
      fetchData();
    } catch (error) {
      toast.error('Failed to update operator');
    }
  };

  const handleToggleOperatorActive = async (op) => {
    try {
      await axios.put(`${API}/admin/department-operators/${op.id}`,
        { active: !op.active },
        { withCredentials: true }
      );
      toast.success(`Operator ${op.active ? 'deactivated' : 'activated'}`);
      fetchData();
    } catch (error) {
      toast.error('Failed to update operator');
    }
  };

  const handleDeleteOperator = async (op) => {
    if (!window.confirm(`Delete operator "${op.name}"?`)) return;
    try {
      await axios.delete(`${API}/admin/department-operators/${op.id}`,
        { withCredentials: true }
      );
      toast.success(`Operator "${op.name}" deleted`);
      fetchData();
    } catch (error) {
      toast.error('Failed to delete operator');
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div 
        className="bg-zinc-900 border border-zinc-700 rounded-lg w-full max-w-4xl max-h-[85vh] shadow-2xl flex flex-col"
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div className="p-4 border-b border-zinc-800 flex items-center justify-between flex-shrink-0">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-cyan-500/20 flex items-center justify-center">
              <Shield className="w-5 h-5 text-cyan-500" />
            </div>
            <div>
              <h2 className="text-lg font-bold text-zinc-100">Admin Panel</h2>
              <p className="text-sm text-zinc-500">Manage departments & operators</p>
            </div>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-zinc-800 rounded-lg">
            <X className="w-5 h-5 text-zinc-400" />
          </button>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-zinc-800 flex-shrink-0">
          <button
            onClick={() => setActiveTab('departments')}
            className={`flex-1 py-3 px-4 text-sm font-bold uppercase tracking-wide transition-colors ${
              activeTab === 'departments' 
                ? 'text-cyan-500 border-b-2 border-cyan-500 bg-cyan-500/5' 
                : 'text-zinc-500 hover:text-zinc-300'
            }`}
          >
            Departments ({departments.length})
          </button>
          <button
            onClick={() => setActiveTab('operators')}
            className={`flex-1 py-3 px-4 text-sm font-bold uppercase tracking-wide transition-colors ${
              activeTab === 'operators' 
                ? 'text-cyan-500 border-b-2 border-cyan-500 bg-cyan-500/5' 
                : 'text-zinc-500 hover:text-zinc-300'
            }`}
          >
            Operators ({operators.length})
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-4">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <RefreshCw className="w-8 h-8 text-cyan-500 animate-spin" />
            </div>
          ) : activeTab === 'departments' ? (
            /* Departments Tab */
            <div className="space-y-3">
              {/* Add Department Button */}
              {!showAddDept && (
                <button
                  onClick={() => setShowAddDept(true)}
                  className="w-full p-3 border-2 border-dashed border-zinc-700 rounded-lg text-zinc-500 hover:border-cyan-500 hover:text-cyan-500 transition-colors flex items-center justify-center gap-2"
                >
                  <Plus className="w-5 h-5" />
                  Add Department
                </button>
              )}

              {/* Add Department Form */}
              {showAddDept && (
                <form onSubmit={handleAddDepartment} className="p-4 bg-zinc-800/50 rounded-lg border border-zinc-700 space-y-3">
                  <h3 className="font-bold text-zinc-200">New Department</h3>
                  <Input
                    placeholder="Department name (e.g., Finishing)"
                    value={newDeptName}
                    onChange={(e) => setNewDeptName(e.target.value)}
                    className="bg-zinc-900 border-zinc-700"
                    required
                  />
                  <Input
                    type="password"
                    placeholder="Password"
                    value={newDeptPassword}
                    onChange={(e) => setNewDeptPassword(e.target.value)}
                    className="bg-zinc-900 border-zinc-700"
                    required
                  />
                  <div className="flex gap-2">
                    <Button type="submit" className="bg-cyan-600 hover:bg-cyan-500">Create</Button>
                    <Button type="button" variant="outline" onClick={() => setShowAddDept(false)} className="border-zinc-700">Cancel</Button>
                  </div>
                </form>
              )}

              {/* Department List */}
              {departments.map((dept) => (
                <div key={dept.id} className="p-4 bg-zinc-800/30 rounded-lg border border-zinc-800">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-lg bg-purple-500/20 flex items-center justify-center">
                        <Factory className="w-5 h-5 text-purple-400" />
                      </div>
                      <div>
                        <h3 className="font-bold text-zinc-100">{dept.display_name || dept.name}</h3>
                        <p className="text-sm text-zinc-500">
                          {operators.filter(op => op.department_id === dept.id).length} operators
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      {editingDept === dept.id ? (
                        <div className="flex items-center gap-2">
                          <Input
                            type="password"
                            placeholder="New password"
                            value={editDeptPassword}
                            onChange={(e) => setEditDeptPassword(e.target.value)}
                            className="bg-zinc-900 border-zinc-700 w-40 h-9"
                          />
                          <Button size="sm" onClick={() => handleUpdateDeptPassword(dept.id)} className="bg-cyan-600 h-9">Save</Button>
                          <Button size="sm" variant="outline" onClick={() => setEditingDept(null)} className="border-zinc-700 h-9">Cancel</Button>
                        </div>
                      ) : (
                        <>
                          <button
                            onClick={() => setEditingDept(dept.id)}
                            className="p-2 hover:bg-zinc-700 rounded-lg text-zinc-400 hover:text-zinc-200"
                            title="Change password"
                          >
                            <Keyboard className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => handleDeleteDepartment(dept)}
                            className="p-2 hover:bg-red-500/20 rounded-lg text-zinc-400 hover:text-red-400"
                            title="Delete department"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            /* Operators Tab */
            <div className="space-y-3">
              {/* Add Operator Button */}
              {!showAddOperator && (
                <button
                  onClick={() => setShowAddOperator(true)}
                  className="w-full p-3 border-2 border-dashed border-zinc-700 rounded-lg text-zinc-500 hover:border-cyan-500 hover:text-cyan-500 transition-colors flex items-center justify-center gap-2"
                >
                  <Plus className="w-5 h-5" />
                  Add Operator
                </button>
              )}

              {/* Add Operator Form */}
              {showAddOperator && (
                <form onSubmit={handleAddOperator} className="p-4 bg-zinc-800/50 rounded-lg border border-zinc-700 space-y-3">
                  <h3 className="font-bold text-zinc-200">New Operator</h3>
                  <Input
                    placeholder="Operator name"
                    value={newOpName}
                    onChange={(e) => setNewOpName(e.target.value)}
                    className="bg-zinc-900 border-zinc-700"
                    required
                  />
                  <select
                    value={newOpDeptId}
                    onChange={(e) => setNewOpDeptId(e.target.value)}
                    className="w-full h-10 px-3 bg-zinc-900 border border-zinc-700 rounded-md text-zinc-200"
                    required
                  >
                    <option value="">Select department</option>
                    {departments.map(d => (
                      <option key={d.id} value={d.id}>{d.name}</option>
                    ))}
                  </select>
                  <div className="flex gap-2">
                    <Button type="submit" className="bg-cyan-600 hover:bg-cyan-500">Add</Button>
                    <Button type="button" variant="outline" onClick={() => setShowAddOperator(false)} className="border-zinc-700">Cancel</Button>
                  </div>
                </form>
              )}

              {/* Operators List - Grouped by Department */}
              {departments.map(dept => {
                const deptOperators = operators.filter(op => op.department_id === dept.id);
                if (deptOperators.length === 0) return null;
                
                return (
                  <div key={dept.id} className="space-y-2">
                    <h3 className="text-xs font-bold uppercase tracking-wider text-zinc-500 px-2 pt-2">
                      {dept.name}
                    </h3>
                    {deptOperators.map(op => (
                      <div 
                        key={op.id} 
                        className={`p-3 rounded-lg border flex items-center justify-between ${
                          op.active 
                            ? 'bg-zinc-800/30 border-zinc-800' 
                            : 'bg-zinc-900/50 border-zinc-800/50 opacity-50'
                        }`}
                      >
                        <div className="flex items-center gap-3">
                          <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                            op.active ? 'bg-cyan-500/20' : 'bg-zinc-700'
                          }`}>
                            <User className={`w-4 h-4 ${op.active ? 'text-cyan-500' : 'text-zinc-500'}`} />
                          </div>
                          {editingOperator === op.id ? (
                            <div className="flex items-center gap-2">
                              <Input
                                value={editOpName}
                                onChange={(e) => setEditOpName(e.target.value)}
                                placeholder="Name"
                                className="bg-zinc-900 border-zinc-700 w-32 h-8"
                              />
                              <select
                                value={editOpDeptId}
                                onChange={(e) => setEditOpDeptId(e.target.value)}
                                className="h-8 px-2 bg-zinc-900 border border-zinc-700 rounded-md text-zinc-200 text-sm"
                              >
                                <option value="">Same dept</option>
                                {departments.map(d => (
                                  <option key={d.id} value={d.id}>{d.name}</option>
                                ))}
                              </select>
                              <Button size="sm" onClick={() => handleUpdateOperator(op.id)} className="bg-cyan-600 h-8 px-2">Save</Button>
                              <Button size="sm" variant="outline" onClick={() => setEditingOperator(null)} className="border-zinc-700 h-8 px-2">X</Button>
                            </div>
                          ) : (
                            <div>
                              <span className="font-medium text-zinc-200">{op.name}</span>
                              {!op.active && <span className="ml-2 text-xs text-red-400">(Inactive)</span>}
                            </div>
                          )}
                        </div>
                        {editingOperator !== op.id && (
                          <div className="flex items-center gap-1">
                            <button
                              onClick={() => {
                                setEditingOperator(op.id);
                                setEditOpName(op.name);
                                setEditOpDeptId('');
                              }}
                              className="p-1.5 hover:bg-zinc-700 rounded text-zinc-400 hover:text-zinc-200"
                              title="Edit"
                            >
                              <FileText className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => handleToggleOperatorActive(op)}
                              className={`p-1.5 rounded ${op.active ? 'hover:bg-amber-500/20 text-zinc-400 hover:text-amber-400' : 'hover:bg-green-500/20 text-zinc-400 hover:text-green-400'}`}
                              title={op.active ? 'Deactivate' : 'Activate'}
                            >
                              {op.active ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4" />}
                            </button>
                            <button
                              onClick={() => handleDeleteOperator(op)}
                              className="p-1.5 hover:bg-red-500/20 rounded text-zinc-400 hover:text-red-400"
                              title="Delete"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-4 border-t border-zinc-800 flex-shrink-0">
          <Button onClick={onClose} variant="outline" className="w-full border-zinc-700">
            Close
          </Button>
        </div>
      </div>
    </div>
  );
};

export default AdminPanelModal;
